// core/stores/creatorsHubStore.ts — Zustand store for the Creators' Hub.
//
// Hydrated at module init from the typed seed (deep-clone so test resets
// don't mutate the imported seed constants).
// Services mutate this store; hooks read it reactively.
// Components must use hooks, not this store directly.

import { create } from 'zustand';
import type { HubDiscussion, HubCollaboration, HubInvite, HubEvent } from '@/core/models/creators-hub';
import {
  seedDiscussions,
  seedCollaborations,
  seedInvites,
  seedEvents,
  seedFeaturedCreatorHandles,
} from '@/core/data/creators-hub';

interface CreatorsHubState {
  discussions: HubDiscussion[];
  collaborations: HubCollaboration[];
  invites: HubInvite[];
  events: HubEvent[];
  featuredCreatorHandles: string[];

  /** Replace all collaborations (used by _resetForTest). */
  setCollaborations: (collaborations: HubCollaboration[]) => void;

  /** Replace all invites (used by _resetForTest). */
  setInvites: (invites: HubInvite[]) => void;

  /** Flip youInterested + adjust interested count on a collaboration. */
  toggleInterest: (id: string) => void;

  /** Remove an invite by id (accept or decline). */
  removeInvite: (id: string) => void;

  /** Reset all mutable state back to seed. */
  reset: () => void;
}

export const useCreatorsHubStore = create<CreatorsHubState>()((set) => ({
  discussions: seedDiscussions.map((d) => ({ ...d })),
  collaborations: seedCollaborations.map((c) => ({ ...c, tags: [...c.tags] })),
  invites: seedInvites.map((inv) => ({ ...inv })),
  events: seedEvents.map((e) => ({ ...e })),
  featuredCreatorHandles: [...seedFeaturedCreatorHandles],

  setCollaborations: (collaborations) => set({ collaborations }),
  setInvites: (invites) => set({ invites }),

  toggleInterest: (id) =>
    set((s) => ({
      collaborations: s.collaborations.map((c) => {
        if (c.id !== id) return c;
        const next = !c.youInterested;
        return {
          ...c,
          youInterested: next,
          interested: c.interested + (next ? 1 : -1),
        };
      }),
    })),

  removeInvite: (id) =>
    set((s) => ({
      invites: s.invites.filter((inv) => inv.id !== id),
    })),

  reset: () =>
    set({
      discussions: seedDiscussions.map((d) => ({ ...d })),
      collaborations: seedCollaborations.map((c) => ({ ...c, tags: [...c.tags] })),
      invites: seedInvites.map((inv) => ({ ...inv })),
      events: seedEvents.map((e) => ({ ...e })),
      featuredCreatorHandles: [...seedFeaturedCreatorHandles],
    }),
}));
